import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, ChevronDown, FileText } from 'lucide-react';
import type { Action, ConditionalConfig, SwitchConfig } from '../../services/workflow.service';
import { ActionType } from '../../services/workflow.service';
import n8nService, { N8nWorkflow } from '@/services/n8n.service';
import { useOrgTeams, useOrgAgents } from './VisualWorkflowBuilder/hooks/useOrgData';
import { TeamPickerHint, AutoAssignConfigFields } from './VisualWorkflowBuilder/panels/ActionConfig';
import { VariablePicker } from './VisualWorkflowBuilder/panels/VariablePicker';
import { apiClient } from '@/services/api-client';
import BranchingActionConfig from './BranchingActionConfig';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Email template interface
interface EmailTemplate {
  id: string;
  name: string;
  shortcut?: string;
  content: string;
  emailSubject?: string;
  variables: string[];
}

interface ActionConfigPanelProps {
  action: Action;
  onChange: (config: Record<string, unknown>) => void;
  onConditionalChange?: (conditional: ConditionalConfig) => void;
  onSwitchChange?: (switchConfig: SwitchConfig) => void;
}

// Required config fields per action type
const REQUIRED_FIELDS: Record<string, string[]> = {
  set_status: ['status'],
  set_priority: ['priority'],
  assign_agent: ['agentId'],
  assign_team: ['teamId'],
  auto_assign: ['strategy'],
  add_tag: ['tag'],
  remove_tag: ['tag'],
  send_email: ['to', 'subject', 'body'],
  send_notification: ['title', 'message'],
  trigger_webhook: ['url', 'method'],
  trigger_n8n_workflow: ['workflowId'],
  create_ticket: ['subject', 'description', 'priority'],
};

// Default config values when switching action types
export function getDefaultConfig(type: ActionType): Record<string, unknown> {
  switch (type) {
    case ActionType.SET_STATUS:
      return { status: 'in_progress' };
    case ActionType.SET_PRIORITY:
      return { priority: 'medium' };
    case ActionType.AUTO_ASSIGN:
      return { strategy: 'round_robin', onlyIfUnassigned: true };
    case ActionType.TRIGGER_WEBHOOK:
      return { url: '', method: 'POST' };
    case ActionType.CREATE_TICKET:
      return { subject: '', description: '', priority: 'medium' };
    case ActionType.TRIGGER_N8N_WORKFLOW:
      return { workflowId: '', customPayload: '' };
    case ActionType.CONDITIONAL:
      return {}; // Config is in the 'conditional' field, not 'config'
    case ActionType.SWITCH:
      return {}; // Config is in the 'switch' field, not 'config'
    case ActionType.STOP_WORKFLOW:
      return {}; // No config needed
    default:
      return {};
  }
}

// Returns list of missing required field labels
export function getMissingConfigFields(action: Action): string[] {
  const required = REQUIRED_FIELDS[action.type] || [];
  return required.filter(
    (field) => !action.config[field] || (typeof action.config[field] === 'string' && (action.config[field] as string).trim() === '')
  );
}

// Shared input styles
const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500';
const selectClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white';
const textareaClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 resize-y dark:placeholder-gray-500';
const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

function ConfigField({ label, children, variablePicker }: { label: string; children: React.ReactNode; variablePicker?: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
        {variablePicker}
      </div>
      {children}
    </div>
  );
}

// Email Template Selector Component
function EmailTemplateSelector({
  value,
  onChange,
  onApplyTemplate,
}: {
  value?: string;
  onChange: (templateId: string) => void;
  onApplyTemplate: (template: EmailTemplate) => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/canned-responses/email-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const selectedTemplate = templates.find((t) => t.id === value);

  const handleSelect = (template: EmailTemplate) => {
    onChange(template.id);
    onApplyTemplate(template);
    setShowDropdown(false);
  };

  return (
    <div>
      <ConfigField label="Email Template (Optional)">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className={`${selectClass} flex items-center justify-between text-left`}
          >
            <span className={selectedTemplate ? '' : 'text-gray-400 dark:text-gray-500'}>
              {selectedTemplate ? (
                <span className="flex items-center gap-2">
                  <FileText className="h-3 w-3" />
                  {selectedTemplate.name}
                </span>
              ) : (
                'Select a template (or write custom below)...'
              )}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
          </button>
          {showDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {loading ? (
                  <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Loading templates...
                  </div>
                ) : templates.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No email templates found. Create one in Settings → Canned Responses.
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        onChange('');
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                    >
                      — No template (custom) —
                    </button>
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleSelect(template)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                          value === template.id
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3 flex-shrink-0" />
                          <span>{template.name}</span>
                          {template.shortcut && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">{template.shortcut}</span>
                          )}
                        </div>
                        {(template.variables?.length ?? 0) > 0 && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate dark:text-gray-500">
                            Variables: {template.variables.slice(0, 3).join(', ')}
                            {template.variables.length > 3 && '...'}
                          </div>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </ConfigField>
    </div>
  );
}

// n8n Workflow Selector Component
function N8nWorkflowSelector({
  value,
  onChange,
  onCustomPayloadChange,
  customPayload,
}: {
  value: string;
  onChange: (workflowId: string) => void;
  onCustomPayloadChange?: (payload: string) => void;
  customPayload?: string;
}) {
  const [workflows, setWorkflows] = useState<N8nWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const response = await n8nService.getWorkflows({ isActive: true, limit: 100 });
      setWorkflows(response.data || []);
    } catch (error) {
      console.error('Failed to load n8n workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const selectedWorkflow = workflows.find((w) => w.id === value);

  return (
    <div className="space-y-2">
      <ConfigField label="n8n Workflow">
        <div className="flex gap-2">
          {!manualMode ? (
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className={`${selectClass} flex items-center justify-between text-left`}
              >
                <span className={selectedWorkflow ? '' : 'text-gray-400 dark:text-gray-500'}>
                  {selectedWorkflow ? selectedWorkflow.name : 'Select a workflow...'}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
              </button>
              {showDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {loading ? (
                      <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Loading workflows...
                      </div>
                    ) : workflows.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No active workflows found.{' '}
                        <button
                          type="button"
                          onClick={() => setManualMode(true)}
                          className="text-primary-600 hover:underline"
                        >
                          Enter ID manually
                        </button>
                      </div>
                    ) : (
                      workflows.map((wf) => (
                        <button
                          key={wf.id}
                          type="button"
                          onClick={() => {
                            onChange(wf.id);
                            setShowDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                            value === wf.id
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${wf.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <span className="font-medium">{wf.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 ml-4 dark:text-gray-400">
                            n8n ID: {wf.n8nWorkflowId}
                          </p>
                        </button>
                      ))
                    )}
                    <div className="border-t border-gray-100 dark:border-gray-600 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => {
                          setManualMode(true);
                          setShowDropdown(false);
                        }}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        Enter workflow ID manually
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <input
              type="text"
              className={`${inputClass} flex-1`}
              placeholder="Enter workflow ID (from Settings > n8n)"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (manualMode) {
                setManualMode(false);
              } else {
                loadWorkflows();
              }
            }}
            className="px-2 py-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-400"
            title={manualMode ? 'Switch to dropdown' : 'Refresh workflows'}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {selectedWorkflow && (
          <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
            n8n Workflow ID: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{selectedWorkflow.n8nWorkflowId}</code>
          </p>
        )}
      </ConfigField>

      {onCustomPayloadChange && (
        <ConfigField label="Custom Payload (JSON, optional)">
          <textarea
            className={textareaClass}
            rows={2}
            placeholder='{"key": "{{ticket.id}}", "status": "{{ticket.status}}"}'
            value={customPayload || ''}
            onChange={(e) => onCustomPayloadChange(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
            Supports template variables like {'{{ticket.id}}'}, {'{{ticket.status}}'}, {'{{customer.email}}'}
          </p>
        </ConfigField>
      )}
    </div>
  );
}

export default function ActionConfigPanel({ action, onChange, onConditionalChange, onSwitchChange }: ActionConfigPanelProps) {
  const config = action.config;
  const { teams, loaded: teamsLoaded } = useOrgTeams();
  const { agents } = useOrgAgents();

  // Handle branching action types
  if (action.type === ActionType.CONDITIONAL || action.type === ActionType.SWITCH || action.type === ActionType.STOP_WORKFLOW) {
    return (
      <BranchingActionConfig
        action={action}
        onConditionalChange={onConditionalChange || (() => {})}
        onSwitchChange={onSwitchChange || (() => {})}
      />
    );
  }

  // Refs for fields that support variable insertion
  const webhookUrlRef = useRef<HTMLInputElement>(null);
  const webhookBodyRef = useRef<HTMLTextAreaElement>(null);
  const emailToRef = useRef<HTMLInputElement>(null);
  const emailSubjectRef = useRef<HTMLInputElement>(null);
  const emailBodyRef = useRef<HTMLTextAreaElement>(null);
  const notificationTitleRef = useRef<HTMLInputElement>(null);
  const notificationMsgRef = useRef<HTMLTextAreaElement>(null);

  const update = (field: string, value: unknown) => {
    onChange({ ...config, [field]: value });
  };

  switch (action.type) {
    case ActionType.SET_STATUS:
      return (
        <div className="mt-2">
          <ConfigField label="Status">
            <ThemedSelect
              className={selectClass}
              value={(config.status as string) || 'in_progress'}
              onChange={(e) => update('status', e.target.value)}
            >
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </ThemedSelect>
          </ConfigField>
        </div>
      );

    case ActionType.SET_PRIORITY:
      return (
        <div className="mt-2">
          <ConfigField label="Priority">
            <ThemedSelect
              className={selectClass}
              value={(config.priority as string) || 'medium'}
              onChange={(e) => update('priority', e.target.value)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </ThemedSelect>
          </ConfigField>
        </div>
      );

    case ActionType.ASSIGN_AGENT:
      return (
        <div className="mt-2">
          <ConfigField label="Agent">
            {agents.length === 0 ? (
              <div className="text-xs text-gray-400 dark:text-gray-500 py-1.5">Loading agents...</div>
            ) : (
              <ThemedSelect
                className={selectClass}
                value={(config.agentId as string) || ''}
                onChange={(e) => update('agentId', e.target.value)}
              >
                <option value="">Select an agent</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.email})
                  </option>
                ))}
              </ThemedSelect>
            )}
          </ConfigField>
        </div>
      );

    case ActionType.AUTO_ASSIGN:
      return (
        <div className="mt-2">
          <AutoAssignConfigFields
            config={config}
            onChange={update}
            teams={teams}
            teamsLoaded={teamsLoaded}
            selectClassName={selectClass}
          />
        </div>
      );

    case ActionType.ASSIGN_TEAM:
      return (
        <div className="mt-2">
          <ConfigField label="Team">
            {!teamsLoaded ? (
              <div className="text-xs text-gray-400 dark:text-gray-500 py-1.5">Loading teams...</div>
            ) : (
              <>
                <ThemedSelect
                  className={selectClass}
                  value={(config.teamId as string) || ''}
                  onChange={(e) => update('teamId', e.target.value)}
                >
                  <option value="">{teams.length === 0 ? 'No teams yet' : 'Select a team'}</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </ThemedSelect>
                <TeamPickerHint
                  loaded={teamsLoaded}
                  hasTeams={teams.length > 0}
                  selectedId={config.teamId as string | undefined}
                  known={teams.some((t) => t.id === config.teamId)}
                />
              </>
            )}
          </ConfigField>
        </div>
      );

    case ActionType.ADD_TAG:
    case ActionType.REMOVE_TAG:
      return (
        <div className="mt-2">
          <ConfigField label="Tag">
            <input
              className={inputClass}
              type="text"
              placeholder={action.type === ActionType.ADD_TAG ? 'Tag to add (e.g., VIP)' : 'Tag to remove'}
              value={(config.tag as string) || ''}
              onChange={(e) => update('tag', e.target.value)}
            />
          </ConfigField>
        </div>
      );

    case ActionType.SEND_EMAIL:
      return (
        <div className="mt-2 space-y-2">
          <EmailTemplateSelector
            value={(config.templateId as string) || ''}
            onChange={(templateId) => update('templateId', templateId)}
            onApplyTemplate={(template) => {
              // Auto-fill subject and body from template in one update
              onChange({
                ...config,
                templateId: template.id,
                subject: template.emailSubject || config.subject,
                body: template.content || config.body,
              });
            }}
          />
          <ConfigField label="To" variablePicker={<VariablePicker textareaRef={emailToRef} onInsert={(val) => update('to', val)} />}>
            <input
              ref={emailToRef}
              className={inputClass}
              type="text"
              placeholder="{{entity.contactEmail}} or email@example.com"
              value={(config.to as string) || ''}
              onChange={(e) => update('to', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Subject" variablePicker={<VariablePicker textareaRef={emailSubjectRef} onInsert={(val) => update('subject', val)} />}>
            <input
              ref={emailSubjectRef}
              className={inputClass}
              type="text"
              placeholder="Email subject"
              value={(config.subject as string) || ''}
              onChange={(e) => update('subject', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Body" variablePicker={<VariablePicker textareaRef={emailBodyRef} onInsert={(val) => update('body', val)} />}>
            <textarea
              ref={emailBodyRef}
              className={textareaClass}
              rows={3}
              placeholder="Email body (supports {{entity.field}} variables)"
              value={(config.body as string) || ''}
              onChange={(e) => update('body', e.target.value)}
            />
          </ConfigField>
        </div>
      );

    case ActionType.SEND_NOTIFICATION:
      return (
        <div className="mt-2 space-y-2">
          <ConfigField label="Title" variablePicker={<VariablePicker textareaRef={notificationTitleRef} onInsert={(val) => update('title', val)} />}>
            <input
              ref={notificationTitleRef}
              className={inputClass}
              type="text"
              placeholder="Notification title"
              value={(config.title as string) || ''}
              onChange={(e) => update('title', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Message" variablePicker={<VariablePicker textareaRef={notificationMsgRef} onInsert={(val) => update('message', val)} />}>
            <textarea
              ref={notificationMsgRef}
              className={textareaClass}
              rows={2}
              placeholder="Notification message (supports {{entity.field}} variables)"
              value={(config.message as string) || ''}
              onChange={(e) => update('message', e.target.value)}
            />
          </ConfigField>
        </div>
      );

    case ActionType.TRIGGER_WEBHOOK:
      return (
        <div className="mt-2 space-y-2">
          <ConfigField label="URL">
            <input
              ref={webhookUrlRef}
              className={inputClass}
              type="text"
              placeholder="https://example.com/webhook"
              value={(config.url as string) || ''}
              onChange={(e) => update('url', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Method">
            <ThemedSelect
              className={selectClass}
              value={(config.method as string) || 'POST'}
              onChange={(e) => update('method', e.target.value)}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
            </ThemedSelect>
          </ConfigField>
          <ConfigField label="Request Body (JSON)" variablePicker={<VariablePicker textareaRef={webhookBodyRef} onInsert={(val) => update('body', val)} jsonMode />}>
            <textarea
              ref={webhookBodyRef}
              className={textareaClass + ' font-mono text-xs'}
              rows={3}
              placeholder='{"ticketId": "{{entity.id}}", "type": "{{entity.ticketTypeName}}"}'
              value={(config.body as string) || ''}
              onChange={(e) => update('body', e.target.value)}
            />
          </ConfigField>
        </div>
      );

    case ActionType.TRIGGER_N8N_WORKFLOW:
      return (
        <div className="mt-2">
          <N8nWorkflowSelector
            value={(config.workflowId as string) || ''}
            onChange={(workflowId) => update('workflowId', workflowId)}
            customPayload={(config.customPayload as string) || ''}
            onCustomPayloadChange={(payload) => update('customPayload', payload)}
          />
        </div>
      );

    case ActionType.CREATE_TICKET:
      return (
        <div className="mt-2 space-y-2">
          <ConfigField label="Subject">
            <input
              className={inputClass}
              type="text"
              placeholder="New ticket subject"
              value={(config.subject as string) || ''}
              onChange={(e) => update('subject', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Description">
            <textarea
              className={textareaClass}
              rows={2}
              placeholder="Ticket description (supports {{ticket.field}} variables)"
              value={(config.description as string) || ''}
              onChange={(e) => update('description', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Priority">
            <ThemedSelect
              className={selectClass}
              value={(config.priority as string) || 'medium'}
              onChange={(e) => update('priority', e.target.value)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </ThemedSelect>
          </ConfigField>
        </div>
      );

    default:
      return null;
  }
}
